import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Phone, Menu, X, ChevronDown, FileText } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ricksLogo from "@/assets/ricks-logo.jpg";
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const navLinks = [{
    path: "/",
    label: "HOME"
  }, {
    path: "/about",
    label: "ABOUT"
  }, {
    path: "/services",
    label: "SERVICES",
    dropdown: [
      { path: "/airport-transportation", label: "Airport Transfers" },
      { path: "/services/corporate-travel", label: "Corporate" },
      { path: "/military-families", label: "Military Families" },
      { path: "/jbsa-military-transportation", label: "JBSA Base Access" },
      { path: "/military-graduation-transportation", label: "Military Graduations" },
      { path: "/camp-bullis-transportation", label: "Camp Bullis" },
      { path: "/wedding-transportation", label: "Weddings" },
      { path: "/services/special-events", label: "Events" },
      { path: "/services/hourly-service", label: "Hourly" },
      { path: "/wine-tours", label: "Wine Tours" },
      { path: "/san-antonio-to-austin", label: "SA to Austin" },
      { path: "/services/funeral-transportation", label: "Funeral" }
    ]
  }, {
    path: "/service-areas",
    label: "AREAS"
  }, {
    path: "/blog",
    label: "BLOG"
  }, {
    path: "/weather",
    label: "WEATHER"
  }, {
    path: "/faq",
    label: "FAQ"
  }, {
    path: "/contact",
    label: "CONTACT"
  }];
  return <><nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={ricksLogo} 
              alt="Rick's Black Car Service San Antonio - Premium Luxury Transportation" 
              className="h-16 md:h-20 w-auto object-contain"
              width="240"
              height="80"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map(link => {
              if (link.dropdown) {
                return (
                  <DropdownMenu key={link.path}>
                    <DropdownMenuTrigger className="flex items-center space-x-1 text-sm font-medium tracking-wide transition-colors hover:text-primary text-foreground/80 focus:outline-none">
                      <span>{link.label}</span>
                      <ChevronDown className="w-4 h-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-background/95 backdrop-blur-md border-border shadow-lg z-[100]">
                      {link.dropdown.map(subLink => (
                        <DropdownMenuItem key={subLink.path} asChild>
                          <Link
                            to={subLink.path}
                            className="text-sm font-medium tracking-wide transition-colors hover:text-primary cursor-pointer text-foreground"
                          >
                            {subLink.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                    isActive(link.path) ? "text-primary" : "text-foreground/80"
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right Section */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="text-foreground/80 hover:text-primary hover:bg-primary/10"
            >
              <a href="tel:+12102967409" aria-label="Call us at 210-296-7409">
                <Phone className="w-5 h-5" />
              </a>
            </Button>
            <Button 
              variant="outline"
              className="relative border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574]/10 font-semibold overflow-hidden" 
              asChild
            >
              <Link to="/request-quote" className="flex items-center space-x-2">
                {/* Pulsing glow */}
                <span 
                  className="absolute inset-0 rounded-md pointer-events-none"
                  style={{
                    boxShadow: '0 0 15px rgba(212, 165, 116, 0.5), 0 0 30px rgba(212, 165, 116, 0.3)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  }}
                />
                {/* Shine sweep effect */}
                <span 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                    animation: 'shineSweep 3s ease-in-out infinite',
                    animationDelay: '1s'
                  }}
                />
                <FileText className="w-4 h-4 relative z-10" />
                <span className="relative z-10">REQUEST INSTANT QUOTE</span>
              </Link>
            </Button>
            <Button 
              className="btn-luxury" 
              asChild
            >
              <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                BOOK NOW
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="lg:hidden text-foreground p-3 -mr-2 min-w-[48px] min-h-[48px] flex items-center justify-center rounded-lg hover:bg-muted/50 active:bg-muted transition-colors" 
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && <div className="lg:hidden py-6 border-t border-border/50 animate-fade-in max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="flex flex-col space-y-1">
              {navLinks.map(link => {
                if (link.dropdown) {
                  return (
                    <div key={link.path} className="space-y-1">
                      <div className="text-sm font-semibold tracking-wide text-primary px-4 py-3">
                        {link.label}
                      </div>
                      <div className="space-y-1">
                        {link.dropdown.map(subLink => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            onClick={() => setIsOpen(false)}
                            className="flex items-center min-h-[48px] px-6 text-base font-medium tracking-wide transition-colors hover:text-primary hover:bg-muted/50 active:bg-muted rounded-lg mx-2 text-foreground/80"
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center min-h-[48px] px-4 text-base font-medium tracking-wide transition-colors hover:text-primary hover:bg-muted/50 active:bg-muted rounded-lg mx-2 ${
                      isActive(link.path) ? "text-primary bg-muted/30" : "text-foreground/80"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
              
              {/* Divider */}
              <div className="h-px bg-border/50 my-4 mx-4" />
              
              {/* Phone link */}
              <a 
                href="tel:+12102967409" 
                className="flex items-center min-h-[48px] px-4 mx-2 space-x-3 text-foreground/80 hover:text-primary hover:bg-muted/50 active:bg-muted transition-colors rounded-lg"
              >
                <Phone className="w-5 h-5" />
                <span className="text-base font-medium">210-296-7409</span>
              </a>
              
              {/* Action Buttons */}
              <div className="px-4 pt-4 space-y-3">
                <Button 
                  variant="outline"
                  className="relative border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574]/10 w-full min-h-[52px] text-base font-semibold overflow-hidden" 
                  asChild
                >
                  <Link to="/request-quote" className="flex items-center justify-center space-x-2" onClick={() => setIsOpen(false)}>
                    {/* Pulsing glow */}
                    <span 
                      className="absolute inset-0 rounded-md pointer-events-none"
                      style={{
                        boxShadow: '0 0 15px rgba(212, 165, 116, 0.5), 0 0 30px rgba(212, 165, 116, 0.3)',
                        animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                      }}
                    />
                    {/* Shine sweep effect */}
                    <span 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
                        animation: 'shineSweep 3s ease-in-out infinite',
                        animationDelay: '1s'
                      }}
                    />
                    <FileText className="w-5 h-5 relative z-10" />
                    <span className="relative z-10">REQUEST INSTANT QUOTE</span>
                  </Link>
                </Button>
                <Button className="btn-luxury w-full min-h-[52px] text-base font-semibold" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    BOOK NOW
                  </a>
                </Button>
              </div>
            </div>
          </div>}
      </div>
    </nav>

    {/* Fixed Mobile BOOK NOW Button - always visible on mobile */}
    <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden p-3 bg-background/95 backdrop-blur-md border-t border-border/50">
      <a
        href="https://ricksblackcarservicesantonio.square.site/"
        target="_blank"
        rel="noopener noreferrer"
        className="btn-luxury flex items-center justify-center w-full min-h-[52px] text-base font-bold tracking-wider rounded-md"
      >
        BOOK NOW
      </a>
    </div>
    </>;
};
export default Navbar;